import { DOCUMENT } from '@angular/common';
import { computed, DestroyRef, inject } from '@angular/core';
import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';

import { ScrollService } from '@core/services/common/scroll.service';
import { ThemeService } from '@core/services/store/common-store/theme.service';
import { fnGetReuseStrategyKeyFn, getDeepReuseStrategyKeyFn } from '@/app/shared/utils/tools';
import { NzSafeAny } from 'ng-zorro-antd/core/types';

export type ReuseHookTypes = '_onReuseInit' | '_onReuseDestroy';

export interface ReuseComponentInstance {
  _onReuseInit: () => void;
  _onReuseDestroy: () => void;
}

export interface ReuseComponentRef {
  instance: ReuseComponentInstance;
}

// Tái sử dụng route
// Tham khảo https://zhuanlan.zhihu.com/p/29823560
// https://blog.csdn.net/weixin_30561425/article/details/96985967?utm_medium=distribute.pc_relevant.none-task-blog-BlogCommendFromMachineLearnPai2-1.control&depth_1-utm_source=distribute.pc_relevant.none-task-blog-BlogCommendFromMachineLearnPai2-1.control
export class SimpleReuseStrategy implements RouteReuseStrategy {
  destroyRef = inject(DestroyRef);
  private readonly doc = inject(DOCUMENT);
  private readonly scrollService = inject(ScrollService);

  // Giới hạn số lượng component cache tối đa để tránh memory leak
  static readonly MAX_CACHE_COUNT = 15;

  // Cache map của mỗi component
  static handlers: Record<string, NzSafeAny> = {};
  // Thứ tự truy cập (LRU) - key gần nhất ở cuối mảng
  static accessOrder: string[] = [];
  // Cache vị trí scroll của mỗi trang
  static scrollHandlers: Record<string, NzSafeAny> = {};

  // Mục đích của tham số này là khi click nút xóa trong tab hiện tại, mặc dù tab đã đóng nhưng khi rời khỏi route vẫn sẽ cache component của tab đã đóng,
  // dùng tham số này để ghi nhớ có cần cache route hiện tại không
  public static waitDelete: string | null;
  themesService = inject(ThemeService); // Dùng để lấy theme
  // Có đa tab hay không, không có đa tab thì không làm route cache
  $isShowTab = computed(() => {
    return this.themesService.$themesOptions().isShowTab;
  });

  public static deleteRouteSnapshot(key: string): void {
    if (SimpleReuseStrategy.handlers[key]) {
      if (SimpleReuseStrategy.handlers[key].componentRef) {
        SimpleReuseStrategy.handlers[key].componentRef.destroy();
      }
      delete SimpleReuseStrategy.handlers[key];
      delete SimpleReuseStrategy.scrollHandlers[key];
    }
    // Xóa khỏi accessOrder
    SimpleReuseStrategy.accessOrder = SimpleReuseStrategy.accessOrder.filter(k => k !== key);
  }

  // Xóa tất cả cache, cần dùng khi logout, không sử dụng đa tab, v.v.
  public static deleteAllRouteSnapshot(route: ActivatedRouteSnapshot): Promise<void> {
    return new Promise(resolve => {
      Object.keys(SimpleReuseStrategy.handlers).forEach(key => {
        SimpleReuseStrategy.deleteRouteSnapshot(key);
      });
      SimpleReuseStrategy.waitDelete = getDeepReuseStrategyKeyFn(route);
      resolve();
    });
  }

  // Có cho phép tái sử dụng route không
  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    const key = fnGetReuseStrategyKeyFn(route);
    // Chỉ detach route có key hợp lệ và đa tab đang bật
    return !!key && route.data['shouldDetach'] !== 'no' && this.$isShowTab();
  }

  // Khi rời khỏi route sẽ trigger, lưu trữ route
  store(route: ActivatedRouteSnapshot, handle: NzSafeAny): void {
    if (route.data['shouldDetach'] === 'no') {
      return;
    }
    const key = fnGetReuseStrategyKeyFn(route);
    // Không lưu nếu key rỗng
    if (!key) {
      return;
    }
    // Nếu route cần xóa là route hiện tại thì không lưu snapshot
    if (SimpleReuseStrategy.waitDelete === key) {
      if (handle && handle.componentRef) {
        this.runHook('_onReuseDestroy', handle.componentRef);
        handle.componentRef.destroy();
      }
      SimpleReuseStrategy.waitDelete = null;
      delete SimpleReuseStrategy.scrollHandlers[key];
      return;
    }
    // Không ghi đè cache hợp lệ bằng null handle (xảy ra với route componentless dùng loadChildren)
    if (!handle) {
      return;
    }

    // Khi rời khỏi route, cache vị trí scroll của trang hiện tại
    // Mặc định đều cần keepScroll, nếu không cần keepScroll mới thêm thuộc tính needKeepScroll:no
    const innerScrollContainer = [];
    if (route.data['needKeepScroll'] !== 'no') {
      const scrollContain = route.data['scrollContain'] ?? [];
      scrollContain.forEach((item: string) => {
        const el = this.doc.querySelector(item)!;
        if (el) {
          const position = this.scrollService.getScrollPosition(el);
          innerScrollContainer.push({ [item]: position });
        }
      });
      innerScrollContainer.push({ window: this.scrollService.getScrollPosition() });
    }

    SimpleReuseStrategy.scrollHandlers[key] = { scroll: innerScrollContainer };
    SimpleReuseStrategy.handlers[key] = handle;

    // Cập nhật LRU access order
    SimpleReuseStrategy.accessOrder = SimpleReuseStrategy.accessOrder.filter(k => k !== key);
    SimpleReuseStrategy.accessOrder.push(key);

    // Evict cache cũ nhất nếu vượt quá giới hạn (LRU)
    while (Object.keys(SimpleReuseStrategy.handlers).length > SimpleReuseStrategy.MAX_CACHE_COUNT) {
      const oldestKey = SimpleReuseStrategy.accessOrder.shift();
      if (oldestKey) {
        SimpleReuseStrategy.deleteRouteSnapshot(oldestKey);
      } else {
        break;
      }
    }

    if (handle && handle.componentRef) {
      this.runHook('_onReuseDestroy', handle.componentRef);
    }
  }

  // Có cho phép khôi phục route không
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const key = fnGetReuseStrategyKeyFn(route);
    return !!key && !!SimpleReuseStrategy.handlers[key];
  }

  // Lấy route đã lưu trữ
  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const key = fnGetReuseStrategyKeyFn(route);
    if (!key || !SimpleReuseStrategy.handlers[key]) {
      return null;
    }
    // Cập nhật LRU - đưa key vừa truy cập lên cuối mảng
    SimpleReuseStrategy.accessOrder = SimpleReuseStrategy.accessOrder.filter(k => k !== key);
    SimpleReuseStrategy.accessOrder.push(key);
    return SimpleReuseStrategy.handlers[key];
  }

  // Vào route trigger, khi là cùng route thì tái sử dụng route
  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    const futureKey = fnGetReuseStrategyKeyFn(future);
    const currKey = fnGetReuseStrategyKeyFn(curr);
    if (!!futureKey && SimpleReuseStrategy.handlers[futureKey]) {
      this.runHook('_onReuseInit', SimpleReuseStrategy.handlers[futureKey].componentRef);
    }

    // Khi cả 2 route đều không có key (route componentless/loadChildren),
    // fallback về so sánh routeConfig mặc định của Angular
    if (!futureKey && !currKey) {
      return future.routeConfig === curr.routeConfig;
    }
    const result = futureKey === currKey;
    // Lazy loading không đọc được data, thông qua phương pháp này drill down đến route cấp thấp nhất
    while (future.firstChild) {
      future = future.firstChild;
    }
    // Lấy lại vì future đã thay đổi trong vòng lặp while ở trên
    const scrollFutureKey = fnGetReuseStrategyKeyFn(future);
    if (!!scrollFutureKey && SimpleReuseStrategy.scrollHandlers[scrollFutureKey]) {
      SimpleReuseStrategy.scrollHandlers[scrollFutureKey].scroll.forEach((elOptionItem: Record<string, [number, number]>) => {
        Object.keys(elOptionItem).forEach(element => {
          setTimeout(() => {
            this.scrollService.scrollToPosition(this.doc.querySelector(element), elOptionItem[element]);
          }, 1);
        });
      });
    }
    return result;
  }

  runHook(method: ReuseHookTypes, comp: ReuseComponentRef): void {
    if (comp == null || !comp.instance) {
      return;
    }
    const compThis = comp.instance;
    const fn = compThis[method];
    if (typeof fn !== 'function') {
      return;
    }
    (fn as () => void).call(compThis);
  }
}
